'use client';

import { MapPin, FileText, Route } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { scaleIn, staggerContainer, fadeSlideUp } from '@/utils/animation/variants';
import { buildImportRoute } from '@/components/location-pages/get-city';
import type { TCity } from '@/shared/types';

type TCityFactsProps = {
  city: TCity;
  className?: string;
};

/**
 * Per-city differentiators (FR-003 / FR-014): voivodeship + registration office — the genuinely
 * city-specific facts — rendered as an on-brand stagger band. The transport route is national
 * (Rotterdam → Lohr → Polska); it's shown as a shared strip with the city only as the delivery
 * destination, not dressed up as a unique per-city fact.
 */
export const CityFacts = ({ city, className = '' }: TCityFactsProps) => {
  const prefersReducedMotion = useReducedMotion();

  const shortFacts = [
    { icon: MapPin, label: 'Województwo', value: city.voivodeship },
    { icon: FileText, label: 'Rejestracja', value: city.registrationOffice },
  ];

  return (
    <m.div
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
      className="flex flex-col gap-4"
    >
      <m.span
        variants={fadeSlideUp}
        className="inline-flex w-fit items-center gap-2 rounded-full border border-action/40 bg-action/10 px-3 py-1 text-xs font-oswald uppercase tracking-[0.2em] text-action"
      >
        Fakty o imporcie do {city.nameGenitive}
      </m.span>

      <div className={`grid gap-4 sm:grid-cols-2 ${className}`}>
        {shortFacts.map(({ icon: Icon, label, value }) => (
          <m.div
            key={label}
            variants={scaleIn}
            className="rounded-2xl border border-neutral-800 bg-backgroundLight/80 p-5 transition-colors duration-300 hover:bg-neutral-900/60"
          >
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-neutral-900 text-action">
              <Icon className="h-5 w-5" aria-hidden />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">{label}</p>
            <p className="mt-1 font-oswald text-white">{value}</p>
          </m.div>
        ))}
      </div>

      <m.div
        variants={fadeSlideUp}
        className="rounded-2xl border border-neutral-800 bg-backgroundLight/80 p-5"
      >
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-neutral-900 text-action">
          <Route className="h-5 w-5" aria-hidden />
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">Trasa transportu</p>
        <p className="mt-1 text-sm leading-relaxed text-neutral-200">{buildImportRoute(city)}</p>
      </m.div>
    </m.div>
  );
};
